<script setup>
import { useCompanyStore, useListStore } from "@/stores";
import { computed, onMounted } from "vue";

const companyStore = useCompanyStore();
const listStore = useListStore();

const keyword = computed({
  get: () => companyStore.filters.keyword,
  set: (value) => (companyStore.filters.keyword = value),
});

const advanced = computed({
  get: () => companyStore.filters.advanced,
  set: (value) => (companyStore.filters.advanced = value),
});

const appliedFilters = computed(() => {
  const adv = advanced.value;
  return Object.values(adv).filter((val) => {
    if (val == null) return false; // null or undefined
    if (Array.isArray(val)) return val.length > 0; // empty array
    if (typeof val === "string") return val.trim() !== ""; // empty string
    return true; // all other non-null, non-empty values
  }).length;
});

const branches = computed(() => listStore.branches);
const companiesSectors = computed(() => listStore.companiesSectors);
const communicationResponsibles = computed(
  () => listStore.communicationResponsibles
);

const handleResetBtnClick = () => {
  companyStore.resetAction();
};

const hideSimpleSearch = computed(() => {
  return false;
});

const variant = "company";

const booleanOptions = [
  { text: "Yes", value: true },
  { text: "No", value: false },
];

onMounted(async () => {
  await listStore.fetchCommunicationResponsibles();
  await listStore.fetchBranches();
  await listStore.fetchCompaniesSectors();
});
</script>

<template>
  <div v-if="!hideSimpleSearch">
    <v-text-field v-model="keyword" name="searchbar" label="Search (by name)" />
  </div>
  <v-expansion-panels class="mb-5">
    <v-expansion-panel :class="`advanced-filters-panel panel-${variant}`">
      <v-expansion-panel-title :class="`title-${variant}`">
        <v-icon icon="mdi-filter" /> <b>Advanced Filters</b>
        <div class="ml-2 text-caption font-weight-bold">
          ({{ appliedFilters }}
          <span v-if="appliedFilters == 1">filter</span>
          <span v-else>filters</span> applied)
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <div class="d-flex ga-5">
          <div class="flex-1-0-0">
            <v-text-field v-model="advanced.name" name="name" label="Name" />
          </div>

          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.date"
              label="Created Date"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.branch"
              label="Branch"
              :items="branches"
              item-title="name"
              item-value="id"
              variant="outlined"
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <Autocomplete
              v-model="advanced.responsible"
              label="Responsible"
              :items="communicationResponsibles"
              :multiple="false"
              :chips="false"
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.talentPlatformProfile"
              label="Has Talent Platform Profile"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              clearable
            />
          </div>
        </div>
        <div class="d-flex ga-5">
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.companySector"
              label="Company Sector"
              :items="companiesSectors"
              item-title="name"
              item-value="id"
              variant="outlined"
              clearable
            />
          </div>
          <div class="flex-1-0-0"></div>
          <div class="flex-1-0-0"></div>
          <div class="flex-1-0-0"></div>
          <div class="flex-1-0-0"></div>
        </div>

        <div class="d-flex ga-5">
          <v-btn
            :color="variant"
            variant="outlined"
            @click="handleResetBtnClick"
          >
            Reset
          </v-btn>
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
